import React, { useState, useEffect, useRef, useCallback } from 'react';
import MessageList from './components/MessageList';
import ChatInput from './components/ChatInput';
import UserNameInput from './components/UserNameInput';
import { BotMessageSquare } from 'lucide-react';
import { useChatApi } from './hooks/useChatApi';

const App = () => {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [isStreaming, setIsStreaming] = useState(false);
  const [userId, setUserId] = useState("");
  const [userName, setUserName] = useState("");
  const [creatingUser, setCreatingUser] = useState(false);
  const [isInitializing, setIsInitializing] = useState(true);
  const [showWelcome, setShowWelcome] = useState(false);
  const messagesEndRef = useRef(null);
  const initialLoadDone = useRef(false);
  
  const { sendMessage, createUser, getUser, getConversationHistory } = useChatApi();

  const resetUserAndUrl = useCallback(() => {
    const newUrl = new URL(window.location.href);
    newUrl.searchParams.delete('user_id');
    window.history.replaceState({}, '', newUrl);
    setUserId('');
    setUserName('');
    setMessages([]);
    initialLoadDone.current = false;
    setIsInitializing(false);
    setShowWelcome(true);
  }, []);

  const loadInitialData = useCallback(async (currentUserId) => {
    try {
      setIsInitializing(true);
      setShowWelcome(false);
      const name = await getUser(currentUserId);
      setUserName(name);
      
      const history = await getConversationHistory(currentUserId);
      
      if (history && history.length > 0) {
        setMessages(history);
      } else {
        setMessages([]);
      }
    } catch (error) {
      console.error('Failed to load initial data:', error);
      setMessages([]);
      if (error.message === 'User not found') {
        resetUserAndUrl();
      }
    } finally {
      setIsInitializing(false);
    }
  }, [getUser, getConversationHistory, resetUserAndUrl]);

  useEffect(() => {
    if (initialLoadDone.current) return;

    const urlParams = new URLSearchParams(window.location.search);
    const currentUserId = urlParams.get('user_id');
    
    if (currentUserId) {
      initialLoadDone.current = true;
      setUserId(currentUserId);
      loadInitialData(currentUserId);
    } else {
      setIsInitializing(false);
      setShowWelcome(true);
    }
  }, [loadInitialData]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const handleCreateUser = useCallback(async (name) => {
    setCreatingUser(true);
    setShowWelcome(false);
    try {
      const newUserId = await createUser(name);
      
      const newUrl = new URL(window.location.href);
      newUrl.searchParams.set('user_id', newUserId);
      window.history.replaceState({}, '', newUrl);
      
      setUserId(newUserId);
      setUserName(name);
      setMessages([]);
      setIsInitializing(false);
    } catch (error) {
      console.error('Failed to create user:', error);
      alert('Failed to create user. Please try again.');
      setShowWelcome(true);
    } finally {
      setCreatingUser(false);
    }
  }, [createUser]);

  const handleSend = useCallback((text) => {
    if (isStreaming || !userId) return;
    
    const userMessage = {
      sender: "user",
      text: text
    };
    
    setMessages(prev => [...prev, userMessage]);
    setIsStreaming(true);
    
    sendMessage(
      text,
      userId,
      setMessages,
      setLoading,
      () => setIsStreaming(false)
    );
  }, [isStreaming, sendMessage, userId]);

  if (isInitializing && !showWelcome) {
    return (
      <div className="h-screen flex items-center justify-center">
        <BotMessageSquare className="animate-pulse text-gray-500" size={40} />
      </div>
    );
  }

  if (!userId) {
    return (
      <UserNameInput 
        onSubmit={handleCreateUser} 
        loading={creatingUser}
        showWelcome={showWelcome}
      />
    );
  }

  return (
    <div className="relative flex flex-col h-screen">
      <div className="flex-grow overflow-y-auto flex justify-center p-4">
        <div className="w-full max-w-[720px] px-4">
          {isInitializing ? (
            <div className="h-full flex items-center justify-center min-h-[calc(100vh-180px)]">
              <BotMessageSquare className="animate-pulse text-blue-500" size={40} />
            </div>
          ) : (
            <>
              {messages.length === 0 ? (
                <div className="h-full flex items-center justify-center min-h-[calc(100vh-180px)]">
                  <div className="text-center">
                    <h1 className="text-2xl font-medium text-gray-800 mb-2">
                      Hi {userName}
                    </h1>
                    <p className="text-gray-600">
                      Ask me anything.
                    </p>
                  </div>
                </div>
              ) : (
                <MessageList messages={messages} loading={loading} />
              )}
            </>
          )}
          <div ref={messagesEndRef} />
        </div>
      </div>

      <ChatInput
        input={input}
        setInput={setInput}
        onSend={handleSend}
        loading={loading}
        isStreaming={isStreaming}
      />
    </div>
  );
};

export default App;